import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { StandardTextFieldProps } from "@mui/material/TextField/TextField";
import { IValidateError, useSharedTranslations } from "../../lib";
import styles from "./textarea.module.scss";

interface Props extends StandardTextFieldProps {
    hint?: string;
    validate?: boolean;
    validateFunc?: (value: string) => IValidateError;
    onValidate?: (error: IValidateError) => void;
    triggerValidate?: number;
}

export const Textarea: React.FC<Props> = ({
                                              hint,
                                              validate,
                                              validateFunc,
                                              onValidate,
                                              triggerValidate,
                                              ...props
                                          }) => {
    const t = useSharedTranslations();
    const { i18n } = useTranslation();
    const [value, setValue] = useState<string>("");
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();

    useEffect(() => {
        if ((triggerValidate || !!value) && validate) {
            validateValue(value);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [triggerValidate, validate, validateFunc]);

    useEffect(() => {
        if (props.value !== value) {
            setValue(props.value as string);
        }
    }, [props.value]);

    useEffect(() => {
        if (isError) {
            validateValue(value);
        }
    }, [i18n.language]);

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const changeVal = e.target.value;
        setValue(changeVal);
        if (validate) {
            validateValue(changeVal);
        }

        props.onChange && props.onChange(e);
    };

    const validateValue = (val: string) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!validateFunc) {
            if (!val) {
                error = true;
                message = t("requiredFieldError");
            }
        } else {
            const validation = validateFunc(val);
            error = !validation.valid;
            message = validation.message;
        }
        setIsError(error);
        setValidationError(message);
        onValidate && onValidate({ valid: !error, message: message });
    };

    return (
        <div className={styles.textArea}>
            <TextField
                {...props}
                fullWidth
                value={value}
                maxRows={props.maxRows ? props.maxRows : 4}
                sx={{
                    "& .MuiInputBase-root": {
                        borderRadius: "var(--hr-border-radius-s)",
                        padding: "var(--hr-space-s) var(--hr-space-m)",
                    },
                    "& .MuiInputBase-inputMultiline": {
                        fontFamily: "var(--hr-typography-font-family-default) !important",
                        fontWeight: 400,
                        fontStyle: "normal",
                        letterSpacing: "unset",
                        lineHeight: "unset",
                        fontSize: 14,
                        color: "var(--hr-color-text-primary)",
                        minHeight: 50,
                        resize: "vertical"
                    },
                    "& .MuiOutlinedInput-input": {
                        letterSpacing: "normal",
                        lineHeight: "normal",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isError ? "var(--hr-color-special-error)" : "var(--hr-color-border-secondary)",
                        borderWidth: 1,
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: isError ? "var(--hr-color-special-error)" : "var(--hr-color-border-primary)",
                        borderWidth: 1,
                    },
                    "& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
                        borderColor: isError ? "var(--hr-color-special-error)" : "var(--hr-color-border-secondary)",
                        borderWidth: 1,
                    },
                }}
                onBlur={() => {
                    if (validate) {
                        validateValue(value);
                    }
                }}
                onChange={onChange}
                multiline
            />
            {isError && <div className="Textinput_view_outline Textinput_state_error">
                <div className={"Textinput-Hint"}>{validationError}</div>
            </div>}
        </div>
    );
};
